import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, GoogleAuthProvider, signInWithPopup, getRedirectResult, updateProfile, sendPasswordResetEmail, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';
import { app } from './config';

const auth = getAuth(app);
let googleRedirectResultPromise;

const updateGoogleUserRecord = (user) => {
  const db = getFirestore();
  const userRef = doc(db, 'users', user.uid);

  getDoc(userRef).then(docSnap => {
    if (!docSnap.exists()) {
      return setDoc(userRef, {
        email: user.email,
        displayName: user.displayName || '',
        photoURL: user.photoURL || '',
        createdAt: new Date().toISOString(),
        provider: 'google',
        welcomeEmailSent: false,
        premium: false
      });
    }

    return setDoc(userRef, {
      lastLogin: new Date().toISOString()
    }, { merge: true });
  }).catch(error => {
    console.warn('⚠️ Firestore update failed (non-blocking):', error);
  });
};

// Sign up with email and password
export const signUpWithEmail = async (email, password, displayName = '', marketingConsent = false) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    if (displayName) {
      await updateProfile(user, { displayName });
    }

    const db = getFirestore();
    // Fire and forget - don't block on Firestore
    setDoc(doc(db, 'users', user.uid), {
      email: user.email,
      displayName: displayName || '',
      createdAt: new Date().toISOString(),
      welcomeEmailSent: false,
      marketingConsent: marketingConsent,
      premium: false
    }).catch(error => {
      console.warn('⚠️ Firestore update failed (non-blocking):', error);
    });

    return { success: true, user };
  } catch (error) {
    console.error('Sign up error:', error.code, error.message);
    return { success: false, error: getErrorMessage(error) };
  }
};

// Sign in with Google
export const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    updateGoogleUserRecord(userCredential.user);

    return { success: true, user: userCredential.user };
  } catch (error) {
    console.error('Google sign in error:', error.code, error.message);
    return { success: false, error: getErrorMessage(error) };
  }
};

export const completeGoogleRedirectSignIn = () => {
  if (!googleRedirectResultPromise) {
    googleRedirectResultPromise = getRedirectResult(auth)
      .then(userCredential => {
        if (!userCredential) return { success: false, noRedirect: true };
        updateGoogleUserRecord(userCredential.user);
        return { success: true, user: userCredential.user };
      })
      .catch(error => {
        console.error('Google redirect sign in error:', error.code, error.message);
        return { success: false, error: getErrorMessage(error) };
      });
  }

  return googleRedirectResultPromise;
};

// Sign in with email and password
export const signInWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { success: true, user: userCredential.user };
  } catch (error) {
    return { success: false, error: getErrorMessage(error) };
  }
};

// Sign out
export const signOutUser = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    console.error('Sign out error:', error);
    return { success: false, error: getErrorMessage(error) };
  }
};

// Alias for backward compatibility
export const logOut = signOutUser;

// Reset password
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    return { success: false, error: getErrorMessage(error) };
  }
};

// Listen to auth state changes
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// Helper function to get user-friendly error messages
const getErrorMessage = (error) => {
  switch (error.code) {
    case 'auth/email-already-in-use':
      return 'This email is already registered. Please sign in instead.';
    case 'auth/invalid-email':
      return 'Invalid email address.';
    case 'auth/operation-not-allowed':
      return 'Email/password accounts are not enabled. Please contact support.';
    case 'auth/weak-password':
      return 'Password is too weak. Please use at least 6 characters.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/user-not-found':
      return 'No account found with this email. Please sign up first.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in popup was closed. Please try again.';
    case 'permission-denied':
      return 'Permission denied. Please check Firestore security rules.';
    default:
      return error.message || 'An error occurred. Please try again.';
  }
};

export { auth };